import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { SERVICES, IMAGES } from "@/data/content";

function ServiceCard({ s, large, onQuote }) {
  const Icon = Icons[s.icon] || Icons.Home;
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5 }}
      data-testid={`service-card-${s.id}`}
      className={`group relative overflow-hidden rounded-md border border-white/10 bg-carbon ${large ? "lg:row-span-2" : ""}`}
    >
      <div className={`relative ${large ? "h-56 lg:h-72" : "h-40"} overflow-hidden`}>
        <img src={IMAGES[s.image]} alt={`${s.title} in Snohomish County, WA`}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
        <div className="absolute inset-0 bg-gradient-to-t from-carbon via-carbon/40 to-transparent" />
        {s.featured && (
          <span className="absolute top-3 left-3 text-[10px] uppercase tracking-wider font-bold bg-honey text-white px-2 py-1 rounded-sm">
            Most Requested
          </span>
        )}
        <div className="absolute bottom-3 left-4 w-11 h-11 rounded-sm bg-honey/15 border border-honey/30 backdrop-blur flex items-center justify-center">
          <Icon className="w-5 h-5 text-honey" />
        </div>
      </div>
      <div className="p-5">
        <h3 className="text-lg font-display font-bold text-white">{s.title}</h3>
        <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{s.blurb}</p>
        <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2">
          <button onClick={onQuote} data-testid={`service-cta-${s.id}`}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-honey hover:gap-2.5 transition-all">
            Get a free quote <Icons.ArrowRight className="w-4 h-4" />
          </button>
          {s.to && (
            <Link to={s.to} data-testid={`service-link-${s.id}`}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-zinc-300 hover:text-honey transition-colors">
              Explore the division <Icons.ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default function Services({ onQuote }) {
  const featured = SERVICES.filter((s) => s.featured);
  const rest = SERVICES.filter((s) => !s.featured);
  return (
    <section id="services" className="relative py-24 honeycomb-bg">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-2xl mb-14">
          <p className="text-xs uppercase tracking-[0.2em] text-honey font-bold mb-3">Comprehensive Care for Your Hive</p>
          <h2 className="text-4xl sm:text-5xl font-display font-black uppercase tracking-tighter text-white">
            Roofing Services in Snohomish, King &amp; Skagit County
          </h2>
          <p className="text-zinc-400 mt-4 text-lg">
            From full roof replacement to commercial flat roofs, we protect Pacific Northwest homes
            and buildings with durable craftsmanship and honest, flat-rate pricing.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <ServiceCard s={featured[0]} large onQuote={onQuote} />
          <ServiceCard s={featured[1]} onQuote={onQuote} />
          <ServiceCard s={featured[2]} onQuote={onQuote} />
          {rest.map((s) => (
            <ServiceCard key={s.id} s={s} onQuote={onQuote} />
          ))}
        </div>
      </div>
    </section>
  );
}
